import React, { useEffect, useState } from "react";

import { filterOptionsWithSynonyms, generateRandomId, useTranslation } from "utils";

import { ExtraInfo, Form, Icon, OptGroup, Option, Select, Message as message } from "components";

export const TillageSeedingForm = ({ forms, index, practice, tillageAndSeedings, year, onDelete, onUpdate, showErrors = true, dataTestId }) => {
  const { t } = useTranslation(["modules"]);
  const [showExplainerDrawer, setShowExplainerDrawer] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const selectedPracticeType = practice.practiceType;
  const rules = [{ required: true, message: false }];

  const form = year
    ? (el) => {
        forms.current[year][index] = el;
      }
    : (el) => {
        forms.current[index] = el;
      };

  const getLabel = (selectedPracticeType) => {
    switch (selectedPracticeType) {
      case "tillage":
        return t("bauEntryDrawer.tillageAndSeeding.tillageName");
      case "seeding":
        return t("bauEntryDrawer.tillageAndSeeding.seedingName");
      default:
        return t("bauEntryDrawer.tillageAndSeeding.practiceName");
    }
  };

  const handlePracticeTypeUpdate = (v) => {
    const parentForm = year ? forms.current[year][index] : forms.current[index];
    parentForm.setFieldsValue({ practiceType: v, practiceId: null });
    onUpdate(index, { practiceType: v, practiceId: null });
  };

  useEffect(() => {
    if (practice?.practiceId && tillageAndSeedings[practice.practiceType]) {
      const practiceDetails = tillageAndSeedings[practice.practiceType]?.find((item) => item.id === practice.practiceId);

      setEnabled(practiceDetails?.enabled);
    }
  }, [practice, tillageAndSeedings]);

  useEffect(() => {
    if (!enabled) {
      message({ content: t("bauEntryDrawer.updateRequiredMessage"), icon: "error" });
    }
  }, [enabled, t]);

  const { practiceId } = practice;

  return (
    <>
      <div className="form-product">
        <div className="form-product-header">
          <div className="icon-row margin-bottom-0">
            <h6>
              {t("bauEntryDrawer.tillageAndSeeding.practice", {
                number: index + 1,
              })}
            </h6>
            <Icon
              iconName="tabler/info-circle"
              size="s"
              className="inline margin-left-25"
              onClick={() => setShowExplainerDrawer(true)}
              mixpanelText={t("bauEntryDrawer.tillageAndSeeding.practice")}
            />
          </div>
          <Icon dataTestId="tllage-seeding-form-trash" iconName="tabler/trash" onClick={() => onDelete(index)} />
        </div>
        <Form
          name={year ? `existing-${year}-${index}-${generateRandomId()}` : `existing-${index}-${generateRandomId()}`}
          ref={form}
          onValuesChange={(_, v) => onUpdate(index, v)}
          className={!showErrors ? "no-errors" : ""}
          dataTestId={`${dataTestId}-form`}
        >
          <div className="form-input-container">
            <Select
              label={t("bauEntryDrawer.tillageAndSeeding.practiceType")}
              name="practiceType"
              initialValue={practice.practiceType}
              onChange={(v) => handlePracticeTypeUpdate(v)}
              rules={rules}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              dataTestId={`${dataTestId}-practiceType`}
            >
              <Option value="tillage">{t("bauEntryDrawer.tillageAndSeeding.tillage")}</Option>
              <Option value="seeding">{t("bauEntryDrawer.tillageAndSeeding.seeding")}</Option>
            </Select>
            <Select
              disabled={!selectedPracticeType}
              label={getLabel(selectedPracticeType)}
              name="practiceId"
              initialValue={practiceId}
              rules={rules}
              showSearch
              optionFilterProp="children"
              filterOption={filterOptionsWithSynonyms}
              className={!enabled ? "disabled-property" : ""}
              dataTestId={`${dataTestId}-practice`}
            >
              {selectedPracticeType && tillageAndSeedings.top[selectedPracticeType].length && (
                <OptGroup key="top" label={t("bauEntryDrawer.tillageAndSeeding.topPractices")}>
                  {tillageAndSeedings.top[selectedPracticeType].map((practice) => (
                    <Option key={`top-${practice.id}`} label={practice.synonyms} value={practice.id}>
                      {practice.name}
                    </Option>
                  ))}
                </OptGroup>
              )}
              <OptGroup key="all" label={t("bauEntryDrawer.tillageAndSeeding.allPractices")}>
                {tillageAndSeedings[selectedPracticeType]
                  ? tillageAndSeedings[selectedPracticeType]
                      .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                      // If the initial value is of a disabled element we want to render it,
                      // all other disabled ones not
                      .filter((practice) => practice.enabled || practice.id === practiceId)
                      .map((practice) => (
                        <Option key={`all-${practice.id}`} label={practice.synonyms} value={practice.id}>
                          {practice.name}
                        </Option>
                      ))
                  : null}
              </OptGroup>
            </Select>
            {!enabled && <span className="disabled-property-message">{t("bauEntryDrawer.tillageAndSeeding.disabledMessage")}</span>}
          </div>
        </Form>
      </div>
      <ExtraInfo type="tillage_seeding-explainer" visible={showExplainerDrawer} setVisible={setShowExplainerDrawer} />
    </>
  );
};
